/**
 * BeHeard simulated app-role profile override — Testing Mode, UI-only.
 *
 * Central's testing-mode API can simulate workspace + role + capability
 * preset, but it doesn't model BeHeard's app-specific role bundles
 * (member_standard / member_premium / member_recruiter / member_trainer /
 * member_org_admin) directly. This override lets a platform-internal
 * tester preview any combination of those bundles by:
 *
 *   1. Storing the chosen profile keys in sessionStorage.
 *   2. BootGate reading them back and computing the matching scope set
 *      from SCOPE_BUNDLES, then injecting those scopes into
 *      bootContext.currentWorkspace.scopes BEFORE the slice updates.
 *   3. Every existing reader (useAppRole, useCentralConsumer.hasCapability,
 *      AppRoleBadge, role badges) sees the simulated scopes through the
 *      normal data path — no call-site changes required.
 *
 * Override is meaningful only when the simulated Central role is `member`.
 * Owner, admin, and viewer have their own role-driven access patterns —
 * the bundle override is silently ignored for those roles.
 *
 * Backend remains authoritative on every API call. The simulation is a
 * UI preview; the server still enforces the real user's capabilities.
 */

import type { AppRoleKey } from '../../../shared/src/appRoles';

const STORAGE_KEY = 'beheard_test_app_profile';

/**
 * Profile keys that are valid in a simulated-member bundle.
 * (owner / admin / viewer / member_custom are excluded — they're
 *  derived states, not bundles you can opt into.)
 */
const VALID_KEYS: readonly AppRoleKey[] = [
  'member_standard',
  'member_premium',
  'member_recruiter',
  'member_trainer',
  'member_org_admin',
];

function isValid(k: string): k is AppRoleKey {
  return (VALID_KEYS as readonly string[]).includes(k);
}

/**
 * DEV-BUILD-ONLY, for the same reason as simulatedCentralRole: the panel that
 * writes these keys is gated on Central-asserted `platformInternal`, but the
 * reader used to ship to production, where any viewer could plant a key and
 * reshape their own scope set. Gated at the accessor so every consumer is
 * covered by one check. Clearing is never gated.
 */
function simulationEnabled(): boolean {
  return Boolean(import.meta.env.DEV);
}

export function getSimulatedAppProfile(): AppRoleKey[] | null {
  if (!simulationEnabled()) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const filtered = parsed.filter((k): k is AppRoleKey => typeof k === 'string' && isValid(k));
    return filtered.length > 0 ? filtered : null;
  } catch {
    return null;
  }
}

export function setSimulatedAppProfile(keys: AppRoleKey[] | null): void {
  // Clearing (null / empty) stays available in every build; setting does not.
  const isClear = !keys || keys.length === 0;
  if (!isClear && !simulationEnabled()) return;
  try {
    if (!keys || keys.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    const filtered = keys.filter(isValid);
    if (filtered.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch {
    // ignore storage errors (private mode, quota)
  }
}

export function clearSimulatedAppProfile(): void {
  setSimulatedAppProfile(null);
}

export { VALID_KEYS as SIMULATABLE_PROFILE_KEYS };
